import type ParcelTemplate from '~/types/ParcelTemplate';

import { type SavedAPIAddress } from '~/types/APIAddress';
import { type RateAtCheckoutErrorArray, type ShippingOptionPayload } from '~/types/RatesAtCheckout';

import { ActionKeys } from './constants';

export const fetchShippingOptions = () =>
  <const>{
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS,
  };

export const fetchShippingOptionsSuccess = (payload: { data: ShippingOptionPayload[] }) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS_SUCCESS,
  };

export const fetchShippingOptionsError = (errorMessage: string) =>
  <const>{
    payload: { errorMessage },
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_SHIPPING_OPTIONS_ERROR,
  };

export const updateShippingOption = (payload: unknown) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION,
  };

export const updateShippingOptionSuccess = (payload: { data: ShippingOptionPayload }) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION_SUCCESS,
  };

export const updateShippingOptionError = (errors: RateAtCheckoutErrorArray) =>
  <const>{
    payload: { errors },
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_SHIPPING_OPTION_ERROR,
  };

export const deleteShippingOption = (payload: string) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION,
  };

export const deleteShippingOptionSuccess = (payload: { data: string }) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION_SUCCESS,
  };

export const deleteShippingOptionError = (errorMessage: string) =>
  <const>{
    payload: { errorMessage },
    type: ActionKeys.RATES_AT_CHECKOUT_DELETE_SHIPPING_OPTION_ERROR,
  };

export const createShippingOption = (payload: unknown) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION,
  };

export const createShippingOptionSuccess = (payload: { data: ShippingOptionPayload }) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION_SUCCESS,
  };

export const createShippingOptionError = (errors: RateAtCheckoutErrorArray) =>
  <const>{
    payload: { errors },
    type: ActionKeys.RATES_AT_CHECKOUT_CREATE_SHIPPING_OPTION_ERROR,
  };

export const openAddShippingOptionModal = () =>
  <const>{
    type: ActionKeys.RATES_AT_CHECKOUT_OPEN_ADD_RULE_MODAL,
  };

export const closeAddShippingOptionModal = () =>
  <const>{
    type: ActionKeys.RATES_AT_CHECKOUT_CLOSE_ADD_RULE_MODAL,
  };

export const fetchLiveRatePackage = () =>
  <const>{
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE,
  };

export const fetchLiveRatePackageSuccess = (payload: { data: { result: ParcelTemplate } }) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE_SUCCESS,
  };

export const fetchLiveRatePackageError = (errorMessage: string) =>
  <const>{
    payload: { errorMessage },
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_PACKAGE_ERROR,
  };

export const updateLiveRatePackage = (payload: string) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE,
  };

export const updateLiveRatePackageSuccess = (payload: { data: ParcelTemplate }) =>
  <const>{
    payload,
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE_SUCCESS,
  };

export const updateLiveRatePackageError = (errorMessage: string) =>
  <const>{
    payload: { errorMessage },
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_PACKAGE_ERROR,
  };

export const fetchLiveRateSenderAddress = () =>
  <const>{
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS,
  };

export const fetchLiveRateSenderAddressSuccess = (senderAddress: SavedAPIAddress) =>
  <const>{
    payload: { senderAddress },
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS_SUCCESS,
  };

export const fetchLiveRateSenderAddressError = (errorMessage: string) =>
  <const>{
    payload: { errorMessage },
    type: ActionKeys.RATES_AT_CHECKOUT_FETCH_LIVE_RATE_SENDER_ADDRESS_ERROR,
  };

export const updateLiveRateSenderAddress = (liveRatesAddressId: number) =>
  <const>{
    payload: { liveRatesAddressId },
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS,
  };

export const updateLiveRateSenderAddressSuccess = (senderAddress: SavedAPIAddress) =>
  <const>{
    payload: { senderAddress },
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS_SUCCESS,
  };

export const updateLiveRateSenderAddressError = (errorMessage: string) =>
  <const>{
    payload: { errorMessage },
    type: ActionKeys.RATES_AT_CHECKOUT_UPDATE_LIVE_RATE_SENDER_ADDRESS_ERROR,
  };

export type RatesAtCheckoutActions =
  | ReturnType<typeof closeAddShippingOptionModal>
  | ReturnType<typeof createShippingOption>
  | ReturnType<typeof createShippingOptionError>
  | ReturnType<typeof createShippingOptionSuccess>
  | ReturnType<typeof deleteShippingOption>
  | ReturnType<typeof deleteShippingOptionError>
  | ReturnType<typeof deleteShippingOptionSuccess>
  | ReturnType<typeof fetchLiveRatePackage>
  | ReturnType<typeof fetchLiveRatePackageError>
  | ReturnType<typeof fetchLiveRatePackageSuccess>
  | ReturnType<typeof fetchLiveRateSenderAddress>
  | ReturnType<typeof fetchLiveRateSenderAddressError>
  | ReturnType<typeof fetchLiveRateSenderAddressSuccess>
  | ReturnType<typeof fetchShippingOptions>
  | ReturnType<typeof fetchShippingOptionsError>
  | ReturnType<typeof fetchShippingOptionsSuccess>
  | ReturnType<typeof openAddShippingOptionModal>
  | ReturnType<typeof updateLiveRatePackage>
  | ReturnType<typeof updateLiveRatePackageError>
  | ReturnType<typeof updateLiveRatePackageSuccess>
  | ReturnType<typeof updateLiveRateSenderAddress>
  | ReturnType<typeof updateLiveRateSenderAddressError>
  | ReturnType<typeof updateLiveRateSenderAddressSuccess>
  | ReturnType<typeof updateShippingOption>
  | ReturnType<typeof updateShippingOptionError>
  | ReturnType<typeof updateShippingOptionSuccess>;
